
import { Component, OnDestroy, Input, Output, EventEmitter, ViewChild, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { finalize, takeWhile } from 'rxjs/operators';
import { PersonService } from '../../services/person.service';
import { LayoutService } from '../../../layouts/layout.service';
// eslint-disable-next-line max-len
import { DeleteConfirmationWithCommentComponent } from '../../../shared/components/dialogs/delete-confirmation-with-comment/delete-confirmation-with-comment.component';
import { InviteService } from '../../../shared/services/invite.service';
import { CompanyLinkingComponent } from '../../../shared/components/dialogs/company-linking/company-linking.component';
import { PersonInfoModalComponent } from '../../../shared/components/dialogs/person-info-modal/person-info-modal.component';
import { CompanyService } from '../../../companies/services/company.service';
import { ApprovalStatus } from '../../enums/approval-status';
import { Person } from '../../models/person.model';
import { AuthService } from '../../../shared/auth.service';
import { BulkReInviteProcessService, BULK_PROCESS_STATE } from '../../../shared/services/bulk-reinvite-process.service';
import { UserRoleCode } from '../../enums/user-role-code';
import { SimplePaginationComponent } from '../../../shared/components/simple-pagination/simple-pagination.component';
import { columnSettings, masterSettings } from './person-list.config';
import { ODataFilter, ODataQuery } from '@zing/grid';
import { ExperienceFlagsService, ExperienceFlagResult } from '../../../shared/services/experience-flags.service';
import { FeatureName } from '../../../shared/enums/feature-name';

@Component({
  selector: 'app-person-list',
  templateUrl: './person-list.component.html',
  styleUrls: ['./person-list.component.scss'],
  standalone: false
})
export class PersonListComponent implements OnDestroy {

  /**
   * If Persons are shown inside of a Company view.
   */
  @Input() companyId: string;
  @Input() companyHasRestrictedAccess = false;
  @Input() showReinviteButton = false;
  @Output() reinviteInProgress = new EventEmitter<boolean>();
  @ViewChild(SimplePaginationComponent) simplePagination!: SimplePaginationComponent;

  columnSettings = columnSettings;
  masterSettings = masterSettings;

  defaultFilter: ODataFilter = {
    logic: 'or',
    filters: [
      { field: 'PersonSource', operator: 'eq', value: 0 },
      { field: 'PersonSource', operator: 'eq', value: 1 },
      { field: 'PersonSource', operator: 'eq', value: 2 },
      { field: 'PersonSource', operator: 'eq', value: 3 },
      { field: 'PersonSource', operator: 'eq', value: 4 }
    ]
  };

  private state: ODataQuery;

  totalCount = 0;
  data: Person[] = [];
  isLoading = false;
  experienceFlagsMap: Map<string, ExperienceFlagResult> = new Map();

  public componentActive = true;
  public totalInvites = 0;
  public sentInvites = 0;
  public isResendInvitationsInProgress = false;

  public hasMore: boolean;

  get isPartnerGatewayAdmin() {
    return this.authService.isPartnerGatewayAdmin;
  }

  get isSuperOrPlatformAdmin() {
    return this.authService.isSuperOrPlatformAdministrator;
  }

  get isViewOnly() {
    return this.authService.isViewOnly;
  }

  get isMembershipAdmin() {
    return this.authService.isMembershipAdmin;
  }

  approvalStatus = ApprovalStatus;

  constructor(
    private layoutService: LayoutService,
    private personService: PersonService,
    private companyService: CompanyService,
    private matAlert: MatDialog,
    private inviteService: InviteService,
    private bulkInviteProcessService: BulkReInviteProcessService,
    private authService: AuthService,
    private cdRef: ChangeDetectorRef,
    private experienceFlagsService: ExperienceFlagsService
  ) {  }

  ngOnDestroy() {
    this.componentActive = false;
  }

  loadData(state: ODataQuery){
    this.state = state;
    this.getInitialPersons(state);
  }

  /**
   * Get all persons from Person API.
   *
   * @param filter ODataQuery instance.
   */
  public getInitialPersons(filter: ODataQuery) {
    this.setIsLoading(true);

    /* If company Id has been provided (Persons inside of Company view)
      then get only Persons which belong to the selected company.
      Otherwise, return all Persons.
    */
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    this.companyId ? this.getPersonsForCompany(filter) : this.getPersons(filter);
  }

  /**
   * Open Delete confirmation dialog.
   *
   * @param personId Identifier of Person instance.
   */
  public openDeleteDialog(personId: string) {
    this.matAlert
      .open(DeleteConfirmationWithCommentComponent)
      .afterClosed()
      .pipe(
        takeWhile(() => this.componentActive)
      )
      .subscribe(
        comment => {
          if (comment) {
            this.deletePerson(personId, comment);
          }
        }
      );
  }

  // /**
  //  * Open invite dialog for sending an invite to potential new user.
  //  */
  // public openInvitePersonDialog() {
  //   this.matAlert
  //     .open(InvitesDialogComponent)
  //     .afterClosed()
  //     .pipe(
  //       takeWhile(() => this.componentActive)
  //     )
  //     .subscribe(
  //       (email: string) => {
  //         if (email) {
  //           this.invitePerson(email);
  //         }
  //       }
  //     );
  // }

  /**
   * Open company linking dialog for linking person with company.
   */
  public openCompanyLinkingDialog() {
    this.matAlert
      .open(CompanyLinkingComponent)
      .afterClosed()
      .pipe(
        takeWhile(() => this.componentActive)
      )
      .subscribe(
        (email: string) => {
          if (email) {
            this.linkPersonWithCompany(email, this.companyId);
          }
        }
      );
  }

  /**
   * Open modal showing additional info (shipments, claims, etc.) for a Person.
   *
   * @param person Person instance.
   */
  public openPersonInfoDialog(person: Person) {
    this.matAlert.open(PersonInfoModalComponent, { data: { person } });
  }

  /**
   * Links Company with Person instance.
   *
   * @param email of the Person which we are linking company to.
   * @param companyId Id of Company.
   */
  public linkPersonWithCompany(email: string, companyId: string) {
    this.setIsLoading(true);

    this.companyService.linkPersonWithCompany(email, companyId)
      .pipe(
        takeWhile(() => this.componentActive)
      ).subscribe(
        isLinkingSuccessful => {
          if (isLinkingSuccessful) {
            this.getPersonsForCompany(this.state); // Refresh data in table.
            this.layoutService.showUIMessage('Company linking is successful.');
          } else {
            this.setIsLoading(false);
            this.layoutService.showUIMessage('Company linking was not successful.');
          }
        },
        () => {
          this.setIsLoading(false);
          this.layoutService.showUIMessage('Company linking was not successful.');
        }
      );
  }

  /**
   * Remove Person with specific Id from database.
   *
   * @param personId Identifier of Person instance.
   */
  public deletePerson(personId: string, comment?: string) {
    this.setIsLoading(true);

    this.personService.delete(personId, comment)
      .pipe(
        takeWhile(() => this.componentActive)
      ).subscribe(
        () => {
          this.setIsLoading(false);
          this.getInitialPersons(this.state); // Reload table data.
          this.layoutService.showUIMessage('Person successfully deleted.');
        },
        () => {
          this.setIsLoading(false);
          this.layoutService.showUIMessage('Person failed to be deleted.');
        }
      );
  }

  /**
   * Get only Persons which belong to the specific Company.
   */
  public getPersonsForCompany(filter: ODataQuery) {
    this.setIsLoading(true);

    this.personService.getPersonsContinuesByCompanyId(filter, this.companyId)
      .pipe(
        takeWhile(() => this.componentActive)
      ).subscribe(
        ({ items, hasMore }) => {
          this.setIsLoading(false);

          this.data = items;
          //this.totalCount = total;
          this.hasMore = hasMore;
          this.loadExperienceFlags(items);
        },
        () => {
          this.setIsLoading(false);

          this.layoutService.showUIMessage('Unable to get persons for this company.');
        }
      );
  }

  /**
   * Get all Persons from server.
   *
   * @param filter ODataQuery instance.
   */
  public getPersons(filter: ODataQuery) {
    this.setIsLoading(true);

    this.personService.getPersonsContinues(filter)
      .pipe(
        takeWhile(() => this.componentActive)
      ).subscribe(
        ({ items, hasMore }) => {
          this.setIsLoading(false);

          this.data = items;
          this.hasMore = hasMore;
          this.loadExperienceFlags(items);
        },
        () => {
          this.setIsLoading(false);

          this.layoutService.showUIMessage('Unable to get persons.');
        }
      );
  }

  /**
   * Resend invite to already invited user
   */
  public resendInvite(email: string) {
    this.setIsLoading(true);
    this.inviteService.resendInvite(email)
      .subscribe(
        () => {
          this.setIsLoading(false);
          this.layoutService.showUIMessage('Person invite has been resent successfully.');
        },
        err => {
          this.setIsLoading(false);
          if (err.status === 404) {
            this.layoutService.showUIMessage('Person invite not found');
          } else {
            this.layoutService.showUIMessage('Person invite has not been resent.');
          }
        }
      );
  }

  /**
   * Send invite to user in pending invite state
   */
  public sendInviteToPendingUser(person: Person) {
    this.setIsLoading(true);
    this.inviteService.sendInvite(person)
      .subscribe(
        () => {
          this.setIsLoading(false);
          this.layoutService.showUIMessage('Person invite has been sent successfully.');
          this.getInitialPersons(this.state);
        },
        () => {
          this.setIsLoading(false);
          this.layoutService.showUIMessage('Person invite has not been sent.');
        }
      );
  }

  /**
   * Resend invites to already invited users in a company
   */
  public resendInvitesForCompany(company: string) {
    this.bulkInviteProcessService.dataReceived.pipe(takeWhile(() => this.isResendInvitationsInProgress),
      finalize(() => {
        this.reinviteInProgress.emit(false);
        this.isResendInvitationsInProgress = false;
      }))
      .subscribe((result: any) => {
        if (result.status === BULK_PROCESS_STATE.EXECUTING) {
          this.totalInvites = result.data.totalInvites;
          this.sentInvites = result.data.sentInvites;
        } else if (result.status === BULK_PROCESS_STATE.FAILURE) {
          this.layoutService.showUIMessage('Bulk Invite process failed!');
          this.isResendInvitationsInProgress = false;
          this.reinviteInProgress.emit(false);
        } else if (result.status === BULK_PROCESS_STATE.COMPLETED) {
          this.totalInvites = 0;
          this.sentInvites = 0;
          this.isResendInvitationsInProgress = false;
          this.reinviteInProgress.emit(false);
          this.layoutService.showUIMessage('Bulk Invite process finished successfully!');
        }
      });

    this.layoutService.showUIMessage(
      'Reinvite requested! Please do not leave this page.'
    );
    this.bulkInviteProcessService
      .processBulkReInviteRequest(this.companyId)
      .pipe(takeWhile(() => this.isResendInvitationsInProgress))
      .subscribe(() => {
        this.reinviteInProgress.emit(true);
        this.getPersonsForCompany(this.state);
      });
  }

  /**
   * Check if invite link can be resent to the user.
   *
   * @param person
   */
  public isPersonReinvitable(person: Person) {
    return person.companyId && person.approvalStatus === 3;
  }

  /**
   * Check if the user in pending invite state.
   *
   * @param person
   */
  public isPersonInPendingInviteState(person: Person) {
    if(person === null){
      return false;
    }
    return person.companyId && person.approvalStatus === 4;
  }

  /**
   * Resends the invitations
   */
  onClickResendInvitations() {
    this.isResendInvitationsInProgress = true;
    this.resendInvitesForCompany(this.companyId);
  }

  /**
   * Check if person has guest role assigned
   *
   * @param person
   */
  isGuestPerson(person: Person) {
    return person
    && person.roles
    && person.roles.length > 0
    && person.roles.some(r => r.userRoles.some(role => role.code === UserRoleCode.JMGuestPerson));
  }

  onPageChange(currentPage: number) {
    this.state.skip = currentPage * this.state.take;
    this.getInitialPersons(this.state);
  }

  private loadExperienceFlags(persons: Person[]): void {
    // Opt-out records are keyed by the identity subject (sub), which equals Person.referenceId.
    // Look up by referenceId, not email (JXP-831 — email never matched the stored records).
    const referenceIds = persons
      .map(p => p.referenceId)
      .filter((id): id is string => !!id);

    if (!referenceIds.length) { return; }

    this.experienceFlagsService.getFlagForUsers(FeatureName.BetaExperienceEnabled, referenceIds)
      .pipe(takeWhile(() => this.componentActive))
      .subscribe(
        flags => {
          this.experienceFlagsMap = new Map(flags.map(f => [f.userId, f]));
          this.cdRef.detectChanges();
        },
        () => {
          this.experienceFlagsMap = new Map();
        }
      );
  }

  setIsLoading(isLoading) {
    this.isLoading = isLoading;
    this.cdRef.detectChanges();
  }
}
